// Main App Component
// Organized for Supabase Migration

import React, { useRef, useState } from 'react';
import { useSketchManager } from './hooks/useSketchManager.js';
import { ControlsPanel } from './components/ControlsPanel.jsx';
import { ExportImportPanel } from './components/ExportImportPanel.jsx';
import { ShapeCategoriesPanel } from './components/ShapeCategoriesPanel.jsx';
import { ParametersPanel } from './components/ParametersPanel.jsx';
import { SketchDisplay } from './components/SketchDisplay.jsx';

const App = () => {
  const [showCoordinates, setShowCoordinates] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(400);
  const [viewportHeight, setViewportHeight] = useState(300);
  const [viewportScale, setViewportScale] = useState(1);
  
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const {
    // State
    selectedSketch,
    currentSketch,
    allSketches,
    groupedSketches,
    expandedCategory,
    showJSON,
    showCode,
    showParameters,
    uploadedImage,
    isAnalyzing,
    
    // Computed values
    getCurrentParams,
    getSketchPoints,
    
    // Actions
    updateParameter,
    handlePointDrag,
    resetParameters,
    exportSketch,
    importSketch,
    copyJSONToClipboard,
    copyCodeToClipboard,
    handleImageUpload,
    toggleCategory,
    selectSketch,
    toggleJSON,
    toggleCode,
    toggleParameters
  } = useSketchManager();

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Mathematical Sketch Generator</h1>
        <p className="text-gray-600">Upload images to auto-generate sketches, import/export JSON, or create geometric shapes manually with real-time editing</p>
        <div className="mt-2 text-sm text-blue-600">
          ✨ <strong>Latest Update:</strong> 6 new advanced sketches including function analysis, bearing navigation, circle theorems, and geometric constructions! Now with {allSketches.length} total shapes.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar */}
        <div className="lg:col-span-1">
          <ControlsPanel 
            showCoordinates={showCoordinates}
            setShowCoordinates={setShowCoordinates}
            showGrid={showGrid}
            setShowGrid={setShowGrid}
            imageInputRef={imageInputRef}
            handleImageUpload={handleImageUpload}
            isAnalyzing={isAnalyzing}
            uploadedImage={uploadedImage}
            viewportWidth={viewportWidth}
            setViewportWidth={setViewportWidth}
            viewportHeight={viewportHeight}
            setViewportHeight={setViewportHeight}
            viewportScale={viewportScale}
            setViewportScale={setViewportScale}
            currentSketch={currentSketch}
            getCurrentParams={getCurrentParams}
            onParamsUpdate={updateParameter}
            getSketchPoints={getSketchPoints}
            onAlignComplete={(alignmentData) => {
              console.log('Alignment completed:', alignmentData);
              // Store alignment in local storage for later retrieval
              const existingAlignments = JSON.parse(localStorage.getItem('sketchAlignments') || '[]');
              existingAlignments.push(alignmentData);
              localStorage.setItem('sketchAlignments', JSON.stringify(existingAlignments));
              console.log('Alignment saved to local storage');
            }}
          />

          <ExportImportPanel 
            currentSketch={currentSketch}
            exportSketch={exportSketch}
            fileInputRef={fileInputRef}
            importSketch={importSketch}
            showJSON={showJSON}
            toggleJSON={toggleJSON}
            showCode={showCode}
            toggleCode={toggleCode}
            showParameters={showParameters}
            toggleParameters={toggleParameters}
          />

          <ShapeCategoriesPanel 
            allSketches={allSketches}
            groupedSketches={groupedSketches}
            expandedCategory={expandedCategory}
            toggleCategory={toggleCategory}
            selectedSketch={selectedSketch}
            selectSketch={selectSketch}
          />
        </div>

        {/* Parameters Panel */}
        <ParametersPanel 
          currentSketch={currentSketch}
          getCurrentParams={getCurrentParams}
          updateParameter={updateParameter}
          resetParameters={resetParameters}
          showParameters={showParameters}
        />

        {/* Main Display */}
        <SketchDisplay 
          currentSketch={currentSketch}
          showCoordinates={showCoordinates}
          showGrid={showGrid}
          getCurrentParams={getCurrentParams}
          handlePointDrag={handlePointDrag}
          showJSON={showJSON}
          showCode={showCode}
          showParameters={showParameters}
          getSketchPoints={getSketchPoints}
          copyJSONToClipboard={copyJSONToClipboard}
          copyCodeToClipboard={copyCodeToClipboard}
          viewportWidth={viewportWidth}
          viewportHeight={viewportHeight}
          viewportScale={viewportScale}
          setViewportHeight={setViewportHeight}
        />
      </div>
    </div>
  );
};

export default App;
